import { useEffect, useState, useRef, useCallback } from 'react'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { Plus, Pencil, Trash2, Search, X, ChevronLeft, ChevronRight, Webhook, Power, PowerOff, EllipsisVertical } from 'lucide-react'
import { webhookSubscriptionApi } from '@/api/services'
import { useToast } from '@/context/ToastContext'
import { Button, buttonVariants } from '@/components/ui/Button'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { Input } from '@/components/ui/Input'
import { Badge, Spinner, EmptyState, Label } from '@/components/ui/misc'
import { cn } from '@/lib/utils'
import { toastApiError } from '@/lib/apiError'
import type { WebhookSubscription } from '@/types'

type FormState = Omit<WebhookSubscription, 'id'>

const TEMPLATE_PUBLISHED_EVENT = 'template.published'
const TEMPLATE_ACTIVE_CHANGED_EVENT = 'template.active.changed'
const EMPTY_FORM: FormState = {
  tenant: '',
  secretKey: '',
  url: '',
  eventType: TEMPLATE_PUBLISHED_EVENT,
  startDateUtc: null,
  endDateUtc: null,
  isEnabled: true,
}
const PAGE_SIZE = 10

function toDateTimeLocal(value?: string | null) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16)
}

function fromDateTimeLocal(value: string) {
  return value ? new Date(value).toISOString() : null
}

export default function WebhookSubscriptionsPage() {
  const { toast } = useToast()

  const [subscriptions, setSubscriptions] = useState<WebhookSubscription[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState<WebhookSubscription | null>(null)
  const [creating, setCreating] = useState(false)
  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [saving, setSaving] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<WebhookSubscription | null>(null)
  const [deleting, setDeleting] = useState(false)

  const searchTimer = useRef<ReturnType<typeof setTimeout>>()
  const searchRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    clearTimeout(searchTimer.current)
    searchTimer.current = setTimeout(() => {
      setDebouncedSearch(search)
      setPage(1)
    }, 350)
    return () => clearTimeout(searchTimer.current)
  }, [search])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await webhookSubscriptionApi.getAll({
        filter: debouncedSearch || undefined,
        pageNumber: page,
        pageSize: PAGE_SIZE,
      })
      setSubscriptions(res.items)
      setTotalCount(res.totalCount)
      setTotalPages(res.totalPages)
    } catch (e: unknown) {
      toastApiError(toast, e, 'Failed to load subscribers')
    } finally {
      setLoading(false)
    }
  }, [debouncedSearch, page, toast])

  useEffect(() => { load() }, [load])

  function openCreate() {
    setForm(EMPTY_FORM)
    setEditing(null)
    setCreating(true)
  }

  function openEdit(subscription: WebhookSubscription) {
    setForm({
      tenant: subscription.tenant,
      secretKey: subscription.secretKey,
      url: subscription.url,
      eventType: subscription.eventType || TEMPLATE_PUBLISHED_EVENT,
      startDateUtc: subscription.startDateUtc ?? null,
      endDateUtc: subscription.endDateUtc ?? null,
      isEnabled: subscription.isEnabled,
    })
    setEditing(subscription)
    setCreating(true)
  }

  function closeForm() {
    setCreating(false)
    setEditing(null)
  }

  async function handleSave() {
    if (!form.tenant || !form.url || !form.eventType) return toast('Tenant, URL and event type are required', 'error')
    setSaving(true)
    try {
      if (editing) {
        await webhookSubscriptionApi.update(editing.id, form)
        toast(`"${form.tenant}" updated`)
      } else {
        await webhookSubscriptionApi.create(form)
        toast(`"${form.tenant}" created`)
      }
      closeForm()
      load()
    } catch (e: unknown) {
      toastApiError(toast, e, 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  async function confirmDelete() {
    if (!deleteTarget) return
    setDeleting(true)
    try {
      await webhookSubscriptionApi.delete(deleteTarget.id)
      toast(`"${deleteTarget.tenant}" deleted`)
      setDeleteTarget(null)
      load()
    } catch (e: unknown) {
      toastApiError(toast, e, 'Delete failed')
    } finally {
      setDeleting(false)
    }
  }

  function clearSearch() {
    setSearch('')
    searchRef.current?.focus()
  }

  const from = totalCount === 0 ? 0 : (page - 1) * PAGE_SIZE + 1
  const to = Math.min(page * PAGE_SIZE, totalCount)

  return (
    <div className="flex h-full overflow-hidden">
      <div className="flex flex-1 flex-col overflow-hidden">
        <div className="flex items-center gap-4 border-b border-border px-6 py-4 bg-surface flex-shrink-0">
          <div className="min-w-0">
            <h1 className="text-sm font-black tracking-wider text-ink">PUBLISH SUBSCRIBERS</h1>
            <p className="text-xs text-muted font-mono mt-0.5">
              {loading ? 'Loading…' : `${totalCount} webhook subscriber${totalCount !== 1 ? 's' : ''}`}
            </p>
          </div>

          <div className="flex-1 max-w-sm relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted pointer-events-none" />
            <input
              ref={searchRef}
              className="w-full rounded-md border border-border2 bg-bg pl-8 pr-8 py-1.5 text-xs font-mono text-ink placeholder:text-muted focus:border-accent focus:outline-none transition-colors"
              placeholder="Search by tenant, URL or event…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && (
              <button onClick={clearSearch} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted hover:text-ink transition-colors">
                <X className="h-3 w-3" />
              </button>
            )}
          </div>

          <div className="flex-1" />
          <Button onClick={openCreate}><Plus className="h-3.5 w-3.5" /> New Subscriber</Button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex justify-center py-20"><Spinner className="h-6 w-6" /></div>
          ) : subscriptions.length === 0 ? (
            <EmptyState message={debouncedSearch ? `No subscribers match "${debouncedSearch}"` : 'No publish subscribers yet.'} />
          ) : (
            <div className="grid gap-3">
              {subscriptions.map(subscription => (
                <div
                  key={subscription.id}
                  className={cn(
                    'flex items-center gap-4 rounded-lg border border-border bg-surface px-5 py-4 hover:border-border2 transition-colors cursor-pointer',
                    !subscription.isEnabled && 'border-red/40 bg-red/5'
                  )}
                  onClick={() => openEdit(subscription)}
                >
                  <Webhook className="h-4 w-4 text-accent2 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 min-w-0">
                      <p className={cn('text-sm font-semibold text-ink truncate', !subscription.isEnabled && 'text-red italic')}>
                        {subscription.tenant}
                      </p>
                      <Badge variant={subscription.isEnabled ? 'green' : 'red'}>
                        {subscription.isEnabled ? 'enabled' : 'disabled'}
                      </Badge>
                      <span className="font-mono text-[10px] font-bold text-yellow bg-yellow/5 border border-yellow/20 rounded px-1.5 py-0.5 flex-shrink-0 tracking-widest">
                        {subscription.eventType}
                      </span>
                    </div>
                    <p className="text-xs text-muted mt-0.5 truncate">{subscription.url}</p>
                  </div>
                  <div className="hidden lg:block flex-shrink-0 text-right">
                    <p className="text-[10px] text-muted font-mono">
                      {subscription.startDateUtc ? `From ${new Date(subscription.startDateUtc).toLocaleDateString()}` : 'No start date'}
                    </p>
                    <p className="text-[10px] text-muted font-mono">
                      {subscription.endDateUtc ? `Until ${new Date(subscription.endDateUtc).toLocaleDateString()}` : 'No end date'}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center" onClick={e => e.stopPropagation()}>
                    <DropdownMenu.Root>
                      <DropdownMenu.Trigger asChild>
                        <button
                          type="button"
                          className={cn(
                            buttonVariants({ variant: 'ghost', size: 'icon' }),
                            'text-muted hover:text-ink'
                          )}
                          aria-label={`Actions for ${subscription.tenant}`}
                          title="Actions"
                        >
                          <EllipsisVertical className="h-4 w-4" />
                        </button>
                      </DropdownMenu.Trigger>
                      <DropdownMenu.Portal>
                        <DropdownMenu.Content
                          align="end"
                          sideOffset={6}
                          className="z-50 min-w-[12.5rem] rounded-lg border border-border bg-surface py-1 shadow-xl"
                        >
                          <DropdownMenu.Item
                            className="flex cursor-pointer items-center gap-2 px-3 py-2 text-xs font-semibold text-ink outline-none data-[highlighted]:bg-bg"
                            onSelect={() => openEdit(subscription)}
                          >
                            <Pencil className="h-3.5 w-3.5 shrink-0 text-muted" />
                            Edit
                          </DropdownMenu.Item>
                          <DropdownMenu.Item
                            className="flex cursor-pointer items-center gap-2 px-3 py-2 text-xs font-semibold text-red outline-none data-[highlighted]:bg-red/10"
                            onSelect={() => setDeleteTarget(subscription)}
                          >
                            <Trash2 className="h-3.5 w-3.5 shrink-0" />
                            Delete
                          </DropdownMenu.Item>
                        </DropdownMenu.Content>
                      </DropdownMenu.Portal>
                    </DropdownMenu.Root>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-border bg-surface px-6 py-3 flex-shrink-0">
            <p className="text-[11px] text-muted font-mono">
              Showing <span className="text-ink">{from}–{to}</span> of <span className="text-ink">{totalCount}</span>
            </p>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon" onClick={() => setPage(p => p - 1)} disabled={page === 1}>
                <ChevronLeft className="h-3.5 w-3.5" />
              </Button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                .reduce<(number | '...')[]>((acc, p, i, arr) => {
                  if (i > 0 && (p as number) - (arr[i - 1] as number) > 1) acc.push('...')
                  acc.push(p)
                  return acc
                }, [])
                .map((p, i) =>
                  p === '...' ? (
                    <span key={`e${i}`} className="w-8 text-center text-xs text-muted">…</span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => setPage(p as number)}
                      className={cn(
                        'h-8 min-w-8 px-2 rounded-md text-xs font-mono transition-colors',
                        page === p ? 'bg-accent text-white font-bold' : 'text-muted hover:text-ink hover:bg-border'
                      )}
                    >{p}</button>
                  )
                )}
              <Button variant="ghost" size="icon" onClick={() => setPage(p => p + 1)} disabled={page === totalPages}>
                <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {creating && (
        <div className="w-96 border-l border-border bg-surface flex flex-col flex-shrink-0">
          <div className="flex items-center justify-between border-b border-border px-5 py-4">
            <h2 className="text-xs font-black tracking-wider text-ink">
              {editing ? 'EDIT SUBSCRIBER' : 'NEW SUBSCRIBER'}
            </h2>
            <Button variant="ghost" size="sm" onClick={closeForm}>Cancel</Button>
          </div>
          <div className="flex-1 overflow-y-auto p-5 space-y-4">
            <div>
              <Label>Tenant</Label>
              <Input placeholder="revo" value={form.tenant} onChange={e => setForm(p => ({ ...p, tenant: e.target.value }))} />
            </div>
            <div>
              <Label>Webhook URL</Label>
              <Input placeholder="https://example.com/api/report-template-published" value={form.url} onChange={e => setForm(p => ({ ...p, url: e.target.value }))} />
            </div>
            <div>
              <Label>Event Type</Label>
              <Input value={form.eventType} onChange={e => setForm(p => ({ ...p, eventType: e.target.value }))} />
              <p className="mt-1 text-[10px] text-muted font-mono">
                Template events: {TEMPLATE_PUBLISHED_EVENT} or {TEMPLATE_ACTIVE_CHANGED_EVENT}
              </p>
            </div>
            <div>
              <Label>Secret Key</Label>
              <Input type="password" placeholder="Sent as x-api-key" value={form.secretKey} onChange={e => setForm(p => ({ ...p, secretKey: e.target.value }))} />
              <p className="mt-1 text-[10px] text-muted font-mono">Leave empty when the subscriber does not require a key.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Start Date</Label>
                <Input type="datetime-local" value={toDateTimeLocal(form.startDateUtc)} onChange={e => setForm(p => ({ ...p, startDateUtc: fromDateTimeLocal(e.target.value) }))} />
              </div>
              <div>
                <Label>End Date</Label>
                <Input type="datetime-local" value={toDateTimeLocal(form.endDateUtc)} onChange={e => setForm(p => ({ ...p, endDateUtc: fromDateTimeLocal(e.target.value) }))} />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Label className="mb-0" id="subscriber-enabled-label">Enabled</Label>
              <button
                type="button"
                role="switch"
                aria-checked={form.isEnabled}
                aria-labelledby="subscriber-enabled-label"
                onClick={() => setForm(p => ({ ...p, isEnabled: !p.isEnabled }))}
                className={cn(
                  'relative h-5 w-10 flex-shrink-0 rounded-full transition-colors',
                  form.isEnabled ? 'bg-accent' : 'bg-border2'
                )}
              >
                <span
                  className={cn(
                    'pointer-events-none absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform',
                    form.isEnabled ? 'translate-x-5' : 'translate-x-0'
                  )}
                />
              </button>
              {form.isEnabled ? <Power className="h-3.5 w-3.5 text-green" /> : <PowerOff className="h-3.5 w-3.5 text-muted" />}
              <span className="text-[10px] text-muted font-mono">{form.isEnabled ? 'receives publishes' : 'paused'}</span>
            </div>
          </div>
          <div className="border-t border-border p-5">
            <Button className="w-full" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : editing ? 'Update Subscriber' : 'Create Subscriber'}
            </Button>
          </div>
        </div>
      )}
      <ConfirmDialog
        open={deleteTarget !== null}
        title="DELETE SUBSCRIBER"
        message={deleteTarget ? `Delete subscriber "${deleteTarget.tenant}"? This action cannot be undone.` : ''}
        loading={deleting}
        onCancel={() => {
          if (!deleting) setDeleteTarget(null)
        }}
        onConfirm={confirmDelete}
      />
    </div>
  )
}
